import { safePolygon, useFloating, useHover, useInteractions } from '@floating-ui/react-dom-interactions'
import { Todo } from 'api/clients/TodoClient'
import { ReactComponent as CommentsIcon } from 'assets/icons/comment16.svg'
import { ReactComponent as LocationPinIcon } from 'assets/icons/locationPin.svg'
import { ReactComponent as TasksIcon } from 'assets/icons/taskCircle.svg'
import cx from 'classnames'
import { COMMENT_VIEW } from 'components/DocRightRail/Constants'
import { IconButtonProps } from 'components/IconButton/Types'
import { observer } from 'mobx-react-lite'
import { forwardRef, useState } from 'react'
import { rootStore } from 'stores/RootStore'
import { Comment } from 'types'
import { InteractionButton } from '../../InteractionButton'
import Styles from './CollabItemInteractionButton.module.scss'

type CollabItemInteractionProps = {
  items: Todo[] | Comment[]
  selectFn: (id: number) => void
}

type CollabItemInteractionButtonProps = { collabType: 'todo' | 'comment' } & CollabItemInteractionProps &
  Partial<IconButtonProps>

const CollabItemList = observer(
  forwardRef<HTMLUListElement, CollabItemInteractionProps>(({ items, selectFn }, ref) => {
    if (items.length === 0) return <ul />

    const handleClick = (item: Todo | Comment) => {
      rootStore.docStore?.rightRailStore.setActiveTab(COMMENT_VIEW)
      selectFn(item.id)
    }

    return (
      <ul className={Styles.list} ref={ref}>
        {items.map((item) => (
          <li
            aria-label="Clickable list item indicating a comment on the image. Opens the comments section to the right of the document and scrolls to the relevant comment."
            className={Styles.locationContainer}
            onClick={() => handleClick(item)}
            key={item.uuid}
          >
            <span
              className={Styles.locationPinContainer}
              aria-label="An icon and number that indicate the position in the list of comments."
            >
              <LocationPinIcon title="location pin icon" />
              {item.position}
            </span>
            <span aria-label="comment author" className={Styles.authorName}>
              {item.creator.display_name}
            </span>
          </li>
        ))}
      </ul>
    )
  }),
)

export const CollabItemInteractionButton = ({
  collabType,
  className,
  items,
  selectFn,
  ...additonalProps
}: CollabItemInteractionButtonProps) => {
  const [showItemList, setShowItemList] = useState(false)
  const { reference, floating, context } = useFloating({
    open: showItemList,
    onOpenChange: setShowItemList,
    placement: 'bottom',
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      delay: {
        open: 500,
        close: 0,
      },
      handleClose: safePolygon({
        blockPointerEvents: false,
      }),
    }),
  ])

  const icon = collabType === 'todo' ? TasksIcon : CommentsIcon

  return (
    <div
      {...getReferenceProps({
        ref: reference,
        className: collabType === 'todo' ? Styles.todo : Styles.comment,
      })}
    >
      <InteractionButton
        {...additonalProps}
        // onPress={() => setShowItemList(!showItemList)}
        className={cx(Styles.taskInteractionButton, className)}
        icon={icon}
      />

      {showItemList && <CollabItemList items={items} selectFn={selectFn} {...getFloatingProps({ ref: floating })} />}
    </div>
  )
}
